import { Dataset, GridRow } from '../../../../types/types';
import {
  DatasetDataApiRequestData,
  ResolvedApiMethod,
  useDatasetDataRes,
} from '../../../types/types';
import { apiRequest } from '../../../utils/apiRequest';
import { getDataParamsString } from '../../../../utils/apiRequests/getDataParamStrings';
import { resolveTemplate } from '../../../../utils/json/resolveTemplate';
import { ExportSorting } from '../types';

type FetchDatasetData = (
  api: string,
  dataset: Dataset,
  lazy?: boolean,
  cachedQueryId?: string,
  forceHeader?: string
) => Promise<useDatasetDataRes>;

type DetailedFetchDatasetData = (
  apiMethod: ResolvedApiMethod,
  dataset: Dataset,
  lazy?: boolean,
  forceHeader?: string
) => Promise<useDatasetDataRes>;

interface FetchExportPageParams {
  dataset: Dataset;
  requestData: DatasetDataApiRequestData;
  filtersWithSelectedRows: Record<string, unknown> | null;
  activeSorting: ExportSorting;
  exportItemNames: string[] | undefined;
  limit: number | undefined;
  offset: number | undefined;
  lazy: boolean;
  cachedQueryId: string | undefined;
  forceHeader: string | undefined;
  fetchData: FetchDatasetData;
  detailedFetchData: DetailedFetchDatasetData;
}

interface ExportPageResult {
  pageRows: GridRow[];
  totalCount: number | undefined;
  cachedQueryId: string | undefined;
}

export const fetchExportPage = async ({
  dataset,
  requestData,
  filtersWithSelectedRows,
  activeSorting,
  exportItemNames,
  limit,
  offset,
  lazy,
  cachedQueryId,
  forceHeader,
  fetchData,
  detailedFetchData,
}: FetchExportPageParams): Promise<ExportPageResult> => {
  if (typeof dataset.apiMethod === 'string') {
    const { apiUrl } = await resolveTemplate(
      { apiUrl: dataset.apiMethod },
      requestData
    );
    const paramsString = getDataParamsString(
      filtersWithSelectedRows,
      activeSorting,
      dataset,
      limit,
      offset,
      exportItemNames
    );
    const result = await fetchData(
      `${apiUrl}${paramsString}`,
      dataset,
      lazy,
      cachedQueryId,
      forceHeader
    );
    return {
      pageRows: (result?.data ?? []) as GridRow[],
      totalCount: result?.totalCount,
      cachedQueryId: result?.cachedQueryId,
    };
  }

  // DetailedApiMethod uses apiRequest so JSON templates see the same request data as grids.
  // requestData carries cachedQueryId only for continuation pages.
  const result = await apiRequest<useDatasetDataRes>(
    dataset.apiMethod,
    requestData,
    (apiMethod) => detailedFetchData(apiMethod, dataset, lazy, forceHeader)
  );
  return {
    pageRows: (result?.data ?? []) as GridRow[],
    totalCount: result?.totalCount,
    cachedQueryId: result?.cachedQueryId,
  };
};
